import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Volume2, Layers, Home, ChevronLeft, Settings, Image, RefreshCw, ShoppingBag, Package } from "lucide-react";
import { SoundManager } from "@/components/user/SoundManager";
import { GameSettings } from "@/components/user/GameSettings";
import { CardBackSettings } from "@/components/user/CardBackSettings";
import { UserPurchases } from "@/components/currency/UserPurchases";
import { cn } from "@/lib/utils";
import { Capacitor } from "@capacitor/core";
import { useQueryClient } from "@tanstack/react-query";
import PullToRefresh from "react-simple-pull-to-refresh";

// Enhanced mobile header specifically for settings pages
function EnhancedSettingsHeader({ title, backPath }: Readonly<{ title: string; backPath: string }>) {
  const [_, setLocation] = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [safePaddingTop, setSafePaddingTop] = useState(0);
  
  useEffect(() => {
    // Handle scroll effect
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    // When in a Capacitor environment, get the safe area insets
    const getSafeAreaInsets = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          // Set a higher default safe area to avoid status bar overlap
          setSafePaddingTop(50);
          
          // Attempt to use status bar information if available
          const capacitorStatusBar = (window as any).Capacitor?.Plugins?.StatusBar;
          if (capacitorStatusBar?.getInfo) {
            try {
              const info = await capacitorStatusBar.getInfo();
              setSafePaddingTop(info?.height ? info.height + 5 : 50);
            } catch (error) {
              console.log("StatusBar plugin not available", error);
            }
          }
        } catch (error) {
          console.error("Error getting safe area insets:", error);
          setSafePaddingTop(50);
        }
      } else {
        setSafePaddingTop(16);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    getSafeAreaInsets();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Parse the title to ensure consistent styling
  const titleParts = title.split(' ');
  const firstWord = titleParts[0];
  const restOfTitle = titleParts.slice(1).join(' ');
  
  return (
    <div 
      className={cn(
        "sticky top-0 z-30 transition-all duration-300",
        scrolled 
          ? "bg-black/90 backdrop-blur-xl shadow-lg" 
          : "bg-gradient-to-b from-black/80 to-transparent"
      )}
      style={{ paddingTop: safePaddingTop ? `${safePaddingTop}px` : undefined }}
    >
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setLocation(backPath)}
            className="rounded-full bg-black/80 hover:bg-black/60 text-white w-10 h-10"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          
          <h1 className="text-2xl font-bold">
            <span className="text-white">{firstWord}</span>{" "}
            <span className="text-green-500">{restOfTitle}</span>
          </h1>
        </div>
        
        <div className="flex items-center gap-3">
        <Button
            variant="ghost"
            size="icon"
            onClick={() => setLocation("/shop")}
            className="rounded-full bg-black/80 hover:bg-black/60 text-white w-10 h-10"
          >
            <ShoppingBag className="h-5 w-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setLocation("/games")}
            className="rounded-full bg-black/80 hover:bg-black/60 text-white w-10 h-10"
          >
            <Home className="h-5 w-5" />
          </Button>
          
          
        </div>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<string>("decks");
  const [_, setLocation] = useLocation();
  const queryClient = useQueryClient();
  
  // Handle pull-to-refresh
  const handleRefresh = async () => {
    // Invalidate queries based on the active tab
    switch (activeTab) {
      case "decks":
        await queryClient.invalidateQueries({ queryKey: ["/api/deck/my-decks"] });
        await queryClient.invalidateQueries({ queryKey: ["game-settings"] });
        break;
      case "sounds":
        await queryClient.invalidateQueries({ queryKey: ["sound-settings"] });
        break;
      case "cardbacks":
        await queryClient.invalidateQueries({ queryKey: ["/api/card-backs/my-card-backs"] });
        break;
      case "purchases":
        await queryClient.invalidateQueries({ queryKey: ["/api/currency/shop/purchases"] });
        break;
      default:
        // Invalidate all settings-related queries
        await queryClient.invalidateQueries({ queryKey: ["deck"] });
        await queryClient.invalidateQueries({ queryKey: ["game-settings"] });
        await queryClient.invalidateQueries({ queryKey: ["sound-settings"] });
        await queryClient.invalidateQueries({ queryKey: ["/api/card-backs/my-card-backs"] });
        await queryClient.invalidateQueries({ queryKey: ["/api/currency/shop/purchases"] });
    }
    return Promise.resolve();
  };

  return (
    
      <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-gray-900 to-black text-white pb-20">
        {/* Mobile-friendly header */}
        <EnhancedSettingsHeader title="My Backpack" backPath="/games"  />

        <PullToRefresh
      onRefresh={handleRefresh}
      pullingContent={
        <div className="text-center p-2">
          <RefreshCw className="h-6 w-6 mx-auto animate-spin text-primary" />
          <p className="text-sm text-white/60 mt-1">Pull to refresh...</p>
        </div>
      }
      refreshingContent={
        <div className="text-center p-4">
          <RefreshCw className="h-6 w-6 mx-auto animate-spin text-primary" />
          <p className="text-sm text-white/60 mt-1">Refreshing settings...</p>
        </div>
      }
    >
        
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-4">
          {/* Tabs */}
          <div className="mb-6">
            <div className="flex rounded-lg overflow-hidden">
              <button
                onClick={() => setActiveTab("decks")}
                className={cn(
                  "flex-1 py-3 flex items-center justify-center",
                  activeTab === "decks"
                    ? "bg-black/40 border-b-2 border-primary text-white font-medium"
                    : "bg-black/20 hover:bg-black/30 text-gray-300"
                )}
              >
                <Layers className="h-5 w-5 mr-2" />
                Decks
              </button>
              
              <button
                onClick={() => setActiveTab("purchases")}
                className={cn(
                  "flex-1 py-3 flex items-center justify-center",
                  activeTab === "purchases"
                    ? "bg-black/40 border-b-2 border-primary text-white font-medium"
                    : "bg-black/20 hover:bg-black/30 text-gray-300"
                )}
              >
                <Package className="h-5 w-5 mr-2" />
                Shop Purchases
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="min-h-[300px]">
            {activeTab === "sounds" && <SoundManager />}
            {activeTab === "decks" && <GameSettings />}
            {activeTab === "cardbacks" && <UserShopPurchases />}
            {activeTab === "purchases" && <UserPurchases />}
          </div>
        </div>

        </PullToRefresh>

      </div>
  );
}
